<template>
  <div id="layout-wrapper">
    <NavigationBar />
    <div class="content-minimize">
      <div class="page-content">
        <div class="container-fluid">
          <!-- Page Header -->
          <div class="row">
            <div class="col-12">
              <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 class="mb-sm-0 font-size-18">Performance Appraisal - Self Assessment</h4>
                <div class="page-title-right">
                  <ol class="breadcrumb m-0">
                    <li class="breadcrumb-item"><a href="javascript: void(0);">Employee Self Service</a></li>
                    <li class="breadcrumb-item active">Self Assessment</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          <!-- Header -->
          <div class="row mb-4">
            <div class="col-12">
              <div class="card border-0 shadow-sm">
                <div class="card-body">
                  <div class="d-flex justify-content-between align-items-center">
                    <div>
                      <h4 class="mb-1">
                        <i class="mdi mdi-file-chart me-2 text-primary"></i>
                        Performance Appraisal - Self Assessment
                      </h4>
                      <p class="text-muted mb-0">{{ appraisalYear }} Annual Performance Review</p>
                    </div>
                    <div class="text-end">
                <span class="badge bg-primary-subtle text-primary px-3 py-2">
                  Status: {{ status }}
                </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Progress Indicator -->
          <div class="row mb-4">
            <div class="col-12">
              <div class="card">
                <div class="card-body">
                  <div class="d-flex justify-content-between align-items-center mb-3">
                    <span class="text-muted small">Progress: {{ progressPercentage }}%</span>
                    <span class="text-muted small">Section {{ currentSection + 1 }} of 6</span>
                  </div>
                  <div class="progress" style="height: 8px;">
                    <div class="progress-bar bg-primary" :style="{ width: progressPercentage + '%' }"></div>
                  </div>
                  <div class="d-flex justify-content-between mt-3">
              <span v-for="(section, index) in sections" :key="index"
                    :class="['badge', index <= currentSection ? 'bg-primary' : 'bg-light text-muted']"
                    style="cursor: pointer;"
                    @click="goToSection(index)">
                {{ section.short }}
              </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Main Form -->
          <div class="row">
            <div class="col-12">
              <div class="card">
                <div class="card-body">

                  <!-- Appraisal Details Header -->
                  <div v-if="currentSection === 0" class="border-bottom pb-4 mb-4">
                    <h5 class="text-primary mb-3">
                      <i class="mdi mdi-information-outline me-2"></i>
                      Appraisal Details
                    </h5>
                    <div class="row">
                      <div class="col-md-6 mb-3">
                        <label class="form-label">Associate Name</label>
                        <input type="text" class="form-control" v-model="employeeInfo.name" disabled>
                      </div>
                      <div class="col-md-6 mb-3">
                        <label class="form-label">Position Title</label>
                        <input type="text" class="form-control" v-model="employeeInfo.position" disabled>
                      </div>
                      <div class="col-md-4 mb-3">
                        <label class="form-label">Department</label>
                        <input type="text" class="form-control" v-model="employeeInfo.department" disabled>
                      </div>
                      <div class="col-md-4 mb-3">
                        <label class="form-label">Period Start Date</label>
                        <input type="date" class="form-control" v-model="appraisal.periodStart" required>
                      </div>
                      <div class="col-md-4 mb-3">
                        <label class="form-label">Period End Date</label>
                        <input type="date" class="form-control" v-model="appraisal.periodEnd" required>
                      </div>
                    </div>
                  </div>

                  <!-- Section A: KPAs -->
                  <div v-if="currentSection === 0">
                    <h5 class="text-primary mb-4">
                      <i class="mdi mdi-target me-2"></i>
                      Section A: Performance - Key Performance Areas
                    </h5>

                    <!-- KPAs Table -->
                    <div class="table-responsive mb-4">
                      <table class="table table-bordered">
                        <thead class="bg-light">
                        <tr>
                          <th width="5%">S.No</th>
                          <th width="20%">Key Performance Area (KPA)</th>
                          <th width="25%">Results Achieved</th>
                          <th width="10%">Self Rating (1-5)</th>
                          <th width="10%">Weightage</th>
                          <th width="5%">Actions</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="(kpa, index) in kpas" :key="index">
                          <td class="text-center">{{ index + 1 }}</td>
                          <td>
                        <textarea v-model="kpa.kpa" class="form-control" rows="3"
                                  placeholder="Enter KPA" required></textarea>
                          </td>
                          <td>
                        <textarea v-model="kpa.resultsAchieved" class="form-control" rows="3"
                                  placeholder="Describe your achievements"></textarea>
                          </td>
                          <td>
                            <select v-model="kpa.selfRating" class="form-select" required>
                              <option value="">Select</option>
                              <option value="5">5 - Outstanding</option>
                              <option value="4">4 - Exceeds Expectations</option>
                              <option value="3">3 - Met Expectations</option>
                              <option value="2">2 - Below Expectations</option>
                              <option value="1">1 - Poor</option>
                            </select>
                          </td>
                          <td>
                            <input type="number" v-model="kpa.weightage" class="form-control"
                                   min="0" max="100" step="0.01">
                          </td>
                          <td class="text-center">
                            <button class="btn btn-sm btn-danger" @click="removeKPA(index)"
                                    v-if="kpas.length > 1">
                              <i class="mdi mdi-delete"></i>
                            </button>
                          </td>
                        </tr>
                        </tbody>
                      </table>
                    </div>

                    <button class="btn btn-outline-primary mb-4" @click="addKPA">
                      <i class="mdi mdi-plus me-1"></i> Add Another KPA
                    </button>

                    <!-- Section A Overall Rating Display -->
                    <div class="alert alert-success" v-if="calculatedSectionA">
                      <div class="d-flex justify-content-between align-items-center">
                        <div>
                          <h6 class="mb-0">
                            <i class="mdi mdi-calculator me-2"></i>
                            Section A: Your Overall Self Rating
                          </h6>
                          <small class="text-muted">Weighted average based on KPA ratings</small>
                        </div>
                        <div class="text-end">
                          <h3 class="mb-0 text-success">{{ calculatedSectionA }}</h3>
                          <small class="text-muted">out of 5.00</small>
                        </div>
                      </div>
                    </div>

                    <!-- Key Organizational Parameters -->
                    <h6 class="text-primary mb-3 mt-4">Key Organizational Parameters</h6>
                    <div class="table-responsive">
                      <table class="table table-bordered">
                        <thead class="bg-light">
                        <tr>
                          <th width="5%">S.No</th>
                          <th width="35%">Question</th>
                          <th width="10%">Answer (1-5)</th>
                          <th width="50%">Associate Comments</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="(param, index) in organizationalParams" :key="index">
                          <td class="text-center">{{ index + 1 }}</td>
                          <td>{{ param.question }}</td>
                          <td>
                            <select v-model="param.answer" class="form-select" required>
                              <option value="">Select</option>
                              <option value="5">5 - Outstanding</option>
                              <option value="4">4 - Exceeds Expectations</option>
                              <option value="3">3 - Met Expectations</option>
                              <option value="2">2 - Below Expectations</option>
                              <option value="1">1 - Poor</option>
                            </select>
                          </td>
                          <td>
                        <textarea v-model="param.associateComments" class="form-control" rows="2"
                                  placeholder="Enter your comments"></textarea>
                          </td>
                        </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <!-- Section B: Critical Success Attributes -->
                  <div v-if="currentSection === 1">
                    <h5 class="text-primary mb-4">
                      <i class="mdi mdi-star me-2"></i>
                      Section B: Performance - Critical Success Attributes
                    </h5>

                    <div class="alert alert-info">
                      <i class="mdi mdi-information-outline me-2"></i>
                      Rate yourself on each attribute using the 1-5 scale.
                    </div>

                    <div class="table-responsive">
                      <table class="table table-bordered">
                        <thead class="bg-light">
                        <tr>
                          <th width="5%">S.No</th>
                          <th width="40%">Attribute</th>
                          <th width="15%">Self Rating (1-5)</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="(attr, index) in attributes" :key="index">
                          <td class="text-center">{{ index + 1 }}</td>
                          <td>
                            <strong>{{ attr.attribute }}</strong>
                            <div class="text-muted small" v-if="attr.description">{{ attr.description }}</div>
                          </td>
                          <td>
                            <select v-model="attr.selfRating" class="form-select" required>
                              <option value="">Select</option>
                              <option value="5">5 - Outstanding</option>
                              <option value="4">4 - Exceeds Expectations</option>
                              <option value="3">3 - Met Expectations</option>
                              <option value="2">2 - Below Expectations</option>
                              <option value="1">1 - Poor</option>
                            </select>
                          </td>
                        </tr>
                        </tbody>
                      </table>
                    </div>

                    <!-- Section B Overall Rating Display -->
                    <div class="alert alert-info" v-if="calculatedSectionB">
                      <div class="d-flex justify-content-between align-items-center">
                        <div>
                          <h6 class="mb-0">
                            <i class="mdi mdi-calculator me-2"></i>
                            Section B: Your Overall Self Rating
                          </h6>
                          <small class="text-muted">Simple average of all attribute ratings</small>
                        </div>
                        <div class="text-end">
                          <h3 class="mb-0 text-info">{{ calculatedSectionB }}</h3>
                          <small class="text-muted">out of 5.00</small>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Section C: Associate Feedback -->
                  <div v-if="currentSection === 2">
                    <h5 class="text-primary mb-4">
                      <i class="mdi mdi-comment-text me-2"></i>
                      Section C: Associate Feedback
                    </h5>

                    <!-- Job Content -->
                    <h6 class="bg-light p-3 mb-3">Job Content</h6>
                    <div class="table-responsive mb-4">
                      <table class="table table-bordered">
                        <thead class="bg-light">
                        <tr>
                          <th width="5%">S.No</th>
                          <th width="35%">Attribute</th>
                          <th width="10%">Yes/No</th>
                          <th width="50%">Remarks</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="(item, index) in jobContent" :key="index">
                          <td class="text-center">{{ index + 1 }}</td>
                          <td>{{ item.attribute }}</td>
                          <td>
                            <select v-model="item.response" class="form-select" required>
                              <option value="">Select</option>
                              <option value="Yes">Yes</option>
                              <option value="No">No</option>
                            </select>
                          </td>
                          <td>
                        <textarea v-model="item.remarks" class="form-control" rows="2"
                                  placeholder="Enter your remarks"></textarea>
                          </td>
                        </tr>
                        </tbody>
                      </table>
                    </div>

                    <!-- Growth Opportunities -->
                    <h6 class="bg-light p-3 mb-3">Growth Opportunities</h6>
                    <div class="table-responsive mb-4">
                      <table class="table table-bordered">
                        <thead class="bg-light">
                        <tr>
                          <th width="5%">S.No</th>
                          <th width="35%">Attribute</th>
                          <th width="10%">Yes/No</th>
                          <th width="50%">Remarks</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="(item, index) in growthOpportunities" :key="index">
                          <td class="text-center">{{ index + 1 }}</td>
                          <td>{{ item.attribute }}</td>
                          <td>
                            <select v-model="item.response" class="form-select" required>
                              <option value="">Select</option>
                              <option value="Yes">Yes</option>
                              <option value="No">No</option>
                            </select>
                          </td>
                          <td>
                        <textarea v-model="item.remarks" class="form-control" rows="2"
                                  placeholder="Enter your remarks"></textarea>
                          </td>
                        </tr>
                        </tbody>
                      </table>
                    </div>

                    <!-- Work Culture -->
                    <h6 class="bg-light p-3 mb-3">Work Culture</h6>
                    <div class="table-responsive">
                      <table class="table table-bordered">
                        <thead class="bg-light">
                        <tr>
                          <th width="5%">S.No</th>
                          <th width="35%">Attribute</th>
                          <th width="10%">Yes/No</th>
                          <th width="50%">Remarks</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="(item, index) in workCulture" :key="index">
                          <td class="text-center">{{ index + 1 }}</td>
                          <td>{{ item.attribute }}</td>
                          <td>
                            <select v-model="item.response" class="form-select" required>
                              <option value="">Select</option>
                              <option value="Yes">Yes</option>
                              <option value="No">No</option>
                            </select>
                          </td>
                          <td>
                        <textarea v-model="item.remarks" class="form-control" rows="2"
                                  placeholder="Enter your remarks"></textarea>
                          </td>
                        </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <!-- Section C Part 2: Colleagues & Training -->
                  <div v-if="currentSection === 3">
                    <h5 class="text-primary mb-4">
                      <i class="mdi mdi-comment-text me-2"></i>
                      Section C: Associate Feedback (Continued)
                    </h5>

                    <!-- Colleagues/Team Members -->
                    <h6 class="bg-light p-3 mb-3">Colleagues/Team Members</h6>
                    <div class="table-responsive mb-4">
                      <table class="table table-bordered">
                        <thead class="bg-light">
                        <tr>
                          <th width="5%">S.No</th>
                          <th width="35%">Attribute</th>
                          <th width="10%">Yes/No</th>
                          <th width="50%">Remarks</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="(item, index) in colleagues" :key="index">
                          <td class="text-center">{{ index + 1 }}</td>
                          <td>{{ item.attribute }}</td>
                          <td>
                            <select v-model="item.response" class="form-select" required>
                              <option value="">Select</option>
                              <option value="Yes">Yes</option>
                              <option value="No">No</option>
                            </select>
                          </td>
                          <td>
                        <textarea v-model="item.remarks" class="form-control" rows="2"
                                  placeholder="Enter your remarks"></textarea>
                          </td>
                        </tr>
                        </tbody>
                      </table>
                    </div>

                    <!-- Corporate Trainings -->
                    <h6 class="bg-light p-3 mb-3">Corporate Trainings</h6>
                    <p class="text-muted">Please prioritize the programs based on your requirements from 1 to 7.</p>
                    <div class="table-responsive">
                      <table class="table table-bordered">
                        <thead class="bg-light">
                        <tr>
                          <th width="5%">S.No</th>
                          <th width="65%">Training Program</th>
                          <th width="30%">Priority (1-7)</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="(training, index) in trainings" :key="index">
                          <td class="text-center">{{ index + 1 }}</td>
                          <td>{{ training.trainingProgram }}</td>
                          <td>
                            <select v-model="training.priority" class="form-select" required>
                              <option value="">Select Priority</option>
                              <option v-for="n in 7" :key="n" :value="n">{{ n }}</option>
                            </select>
                          </td>
                        </tr>
                        </tbody>
                      </table>
                    </div>

                    <!-- Additional Feedback Questions -->
                    <h6 class="bg-light p-3 mb-3 mt-4">Additional Feedback</h6>
                    <div class="mb-3">
                      <label class="form-label fw-bold">
                        To increase my effectiveness and impact what are the things I should do:
                      </label>
                      <textarea v-model="additionalFeedback.thingsIShouldDo" class="form-control" rows="4"
                                placeholder="Enter your thoughts..."></textarea>
                    </div>
                    <div class="mb-3">
                      <label class="form-label fw-bold">
                        My manager should do:
                      </label>
                      <textarea v-model="additionalFeedback.managerShouldDo" class="form-control" rows="4"
                                placeholder="Enter your thoughts..."></textarea>
                    </div>
                    <div class="mb-3">
                      <label class="form-label fw-bold">
                        The organization should do:
                      </label>
                      <textarea v-model="additionalFeedback.organizationShouldDo" class="form-control" rows="4"
                                placeholder="Enter your thoughts..."></textarea>
                    </div>
                  </div>

                  <!-- Section E: Goal Settings -->
                  <div v-if="currentSection === 4">
                    <h5 class="text-primary mb-4">
                      <i class="mdi mdi-flag-checkered me-2"></i>
                      Section E: Goal Settings for Next Review Period
                    </h5>

                    <div class="alert alert-info">
                      <i class="mdi mdi-information-outline me-2"></i>
                      Set clear, measurable goals for the upcoming review period.
                    </div>

                    <div class="table-responsive mb-4">
                      <table class="table table-bordered">
                        <thead class="bg-light">
                        <tr>
                          <th width="5%">S.No</th>
                          <th width="70%">Action Plan</th>
                          <th width="20%">Target Date</th>
                          <th width="5%">Actions</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="(goal, index) in goals" :key="index">
                          <td class="text-center">{{ index + 1 }}</td>
                          <td>
                        <textarea v-model="goal.actionPlan" class="form-control" rows="2"
                                  placeholder="Enter action plan" required></textarea>
                          </td>
                          <td>
                            <input type="date" v-model="goal.targetDate" class="form-control" required>
                          </td>
                          <td class="text-center">
                            <button class="btn btn-sm btn-danger" @click="removeGoal(index)"
                                    v-if="goals.length > 1">
                              <i class="mdi mdi-delete"></i>
                            </button>
                          </td>
                        </tr>
                        </tbody>
                      </table>
                    </div>

                    <button class="btn btn-outline-primary" @click="addGoal">
                      <i class="mdi mdi-plus me-1"></i> Add Another Goal
                    </button>
                  </div>

                  <!-- Section F: Review & Submit -->
                  <div v-if="currentSection === 5">
                    <h5 class="text-primary mb-4">
                      <i class="mdi mdi-check-circle me-2"></i>
                      Review & Submit
                    </h5>

                    <div class="alert alert-warning">
                      <i class="mdi mdi-alert me-2"></i>
                      Please review all sections before submitting. Once submitted, your appraisal will be sent to your appraiser for review.
                    </div>

                    <!-- Summary Cards -->
                    <div class="row mb-4">
                      <div class="col-md-3">
                        <div class="card bg-success text-white">
                          <div class="card-body text-center">
                            <h3 class="text-white">{{ calculatedSectionA || '-' }}</h3>
                            <p class="mb-0">Section A Rating</p>
                            <small>KPA Performance</small>
                          </div>
                        </div>
                      </div>
                      <div class="col-md-3">
                        <div class="card bg-info text-white">
                          <div class="card-body text-center">
                            <h3 class="text-white">{{ calculatedSectionB || '-' }}</h3>
                            <p class="mb-0">Section B Rating</p>
                            <small>Attributes</small>
                          </div>
                        </div>
                      </div>
                      <div class="col-md-3">
                        <div class="card bg-primary text-white">
                          <div class="card-body text-center">
                            <h3 class="text-white">{{ goals.length }}</h3>
                            <p class="mb-0">Goals Set</p>
                            <small>Next Period</small>
                          </div>
                        </div>
                      </div>
                      <div class="col-md-3">
                        <div class="card bg-warning text-white">
                          <div class="card-body text-center">
                            <h3 class="text-white">{{ completionPercentage }}%</h3>
                            <p class="mb-0">Completion</p>
                            <small>Overall Progress</small>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Sections Review -->
                    <div class="card mb-3" v-for="(section, index) in sections" :key="index">
                      <div class="card-header d-flex justify-content-between align-items-center">
                  <span>
                    <i :class="['mdi', section.icon, 'me-2']"></i>
                    {{ section.name }}
                  </span>
                        <span :class="['badge', getSectionCompletionBadge(index)]">
                    {{ getSectionStatus(index) }}
                  </span>
                      </div>
                    </div>

                    <!-- Final Comments -->
                    <div class="mb-4">
                      <label class="form-label fw-bold">Any additional comments or concerns:</label>
                      <textarea v-model="appraisal.associateComments" class="form-control" rows="4"
                                placeholder="Optional: Add any final comments..."></textarea>
                    </div>

                    <!-- Digital Signature -->
                    <div class="card bg-light mb-4">
                      <div class="card-body">
                        <h6 class="mb-3">Digital Signature</h6>
                        <div class="form-check mb-3">
                          <input class="form-check-input" type="checkbox" v-model="signatureAgreement" id="signatureCheck">
                          <label class="form-check-label" for="signatureCheck">
                            I certify that the information provided in this self-assessment is accurate and complete to the best of my knowledge.
                          </label>
                        </div>
                        <div v-if="signatureAgreement" class="border p-3 bg-white">
                          <p class="mb-2"><strong>Employee Name:</strong> {{ employeeInfo.name }}</p>
                          <p class="mb-2"><strong>Date:</strong> {{ new Date().toLocaleDateString() }}</p>
                          <p class="mb-0 text-muted small">
                            <i class="mdi mdi-shield-check me-1"></i>
                            Digital signature captured
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Navigation Buttons -->
                  <div class="d-flex justify-content-between mt-4 pt-3 border-top">
                    <div>
                      <button class="btn btn-outline-secondary" @click="previousSection" v-if="currentSection > 0">
                        <i class="mdi mdi-arrow-left me-1"></i> Previous
                      </button>
                    </div>
                    <div>
                      <button class="btn btn-outline-primary me-2" @click="saveDraft" :disabled="saving">
                        <i class="mdi mdi-content-save me-1"></i>
                        {{ saving ? 'Saving...' : 'Save as Draft' }}
                      </button>
                      <button class="btn btn-primary" @click="nextSection" v-if="currentSection < sections.length - 1">
                        Next <i class="mdi mdi-arrow-right ms-1"></i>
                      </button>
                      <button class="btn btn-success" @click="submitAppraisal" v-if="currentSection === sections.length - 1"
                              :disabled="!signatureAgreement || submitting">
                        <i class="mdi mdi-send me-1"></i>
                        {{ submitting ? 'Submitting...' : 'Submit for Review' }}
                      </button>
                    </div>
                  </div>

                </div>
              </div>
            </div>
          </div>

          <!-- Rating Guide Modal -->
          <div class="modal fade" id="ratingGuideModal" tabindex="-1">
            <div class="modal-dialog modal-lg">
              <div class="modal-content">
                <div class="modal-header">
                  <h5 class="modal-title">Rating Scale Guide</h5>
                  <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
                </div>
                <div class="modal-body">
                  <div class="table-responsive">
                    <table class="table table-bordered">
                      <thead>
                      <tr>
                        <th>Rating</th>
                        <th>Description</th>
                      </tr>
                      </thead>
                      <tbody>
                      <tr>
                        <td><strong>5 - Outstanding</strong></td>
                        <td>
                          • Met all aspects of job requirements and exceeded expectations in all aspects<br>
                          • Made major and notable contributions<br>
                          • Demonstrated creativity, initiative, flexibility
                        </td>
                      </tr>
                      <tr>
                        <td><strong>4 - Exceeds Expectations</strong></td>
                        <td>
                          • Met all aspects of job description and exceeded in most respects<br>
                          • Made contributions to the company
                        </td>
                      </tr>
                      <tr>
                        <td><strong>3 - Met Expectations</strong></td>
                        <td>
                          • Met basic job requirements<br>
                          • Task completed within time frame with efficiency and initiative<br>
                          • Fully accomplished expectations
                        </td>
                      </tr>
                      <tr>
                        <td><strong>2 - Below Expectations</strong></td>
                        <td>
                          • Did not consistently meet job requirements<br>
                          • Needs improvement in key areas
                        </td>
                      </tr>
                      <tr>
                        <td><strong>1 - Poor</strong></td>
                        <td>
                          • Did not meet minimum job requirements<br>
                          • Significant performance issues
                        </td>
                      </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
        <!-- end container-fluid -->
      </div>
      <!-- End Page-content -->
    </div>
    <!-- end content-minimize -->
  </div>
  <!-- end layout-wrapper -->
</template>

<script>
/* eslint-disable */
import { authUrl, execute } from "@/api";
import NavigationBar from "@/components/Navigation";

export default {
  name: "EmployeeSelfAssessment",

  components: {
    NavigationBar
  },

  data() {
    return {
      loading: false,
      saving: false,
      submitting: false,
      currentSection: 0,
      appraisalId: null,
      status: 'Draft',
      appraisalYear: new Date().getFullYear(),
      signatureAgreement: false,

      sections: [
        { name: 'Section A: Key Performance Areas', short: 'A: KPAs', icon: 'mdi-target' },
        { name: 'Section B: Critical Success Attributes', short: 'B: Attributes', icon: 'mdi-star' },
        { name: 'Section C: Associate Feedback (Part 1)', short: 'C: Feedback 1', icon: 'mdi-comment' },
        { name: 'Section C: Associate Feedback (Part 2)', short: 'C: Feedback 2', icon: 'mdi-comment-text' },
        { name: 'Section E: Goal Settings', short: 'E: Goals', icon: 'mdi-flag' },
        { name: 'Review & Submit', short: 'Review', icon: 'mdi-check-circle' }
      ],

      employeeInfo: {
        id: null,
        name: '',
        position: '',
        department: '',
        location: '',
        dateOfJoining: ''
      },

      appraisal: {
        periodStart: '',
        periodEnd: '',
        associateComments: ''
      },

      kpas: [],
      organizationalParams: [],

      attributes: [],

      jobContent: [],

      growthOpportunities: [],

      workCulture: [],

      colleagues: [],

      trainings: [],

      additionalFeedback: {
        thingsIShouldDo: '',
        managerShouldDo: '',
        organizationShouldDo: ''
      },

      goals: []
    };
  },

  computed: {
    progressPercentage() {
      return Math.round(((this.currentSection + 1) / this.sections.length) * 100);
    },

    completionPercentage() {
      let completed = 0;
      let total = 6;

      // Check each section
      if (this.kpas.length > 0 && this.kpas.every(k => k.kpa && k.selfRating)) completed++;
      if (this.attributes.every(a => a.selfRating)) completed++;
      if (this.jobContent.every(j => j.response)) completed++;
      if (this.colleagues.every(c => c.response)) completed++;
      if (this.trainings.every(t => t.priority)) completed++;
      if (this.goals.length > 0 && this.goals.every(g => g.actionPlan && g.targetDate)) completed++;

      return Math.round((completed / total) * 100);
    },

    // Auto-calculate Section A Overall Self Rating (Weighted Average)
    calculatedSectionA() {
      if (!this.kpas || this.kpas.length === 0) return null;

      let totalWeightedScore = 0;
      let totalWeight = 0;

      for (const kpa of this.kpas) {
        if (kpa.selfRating && kpa.weightage) {
          const rating = parseFloat(kpa.selfRating);
          const weight = parseFloat(kpa.weightage);
          totalWeightedScore += (rating * weight);
          totalWeight += weight;
        }
      }

      if (totalWeight === 0) return null;
      return (totalWeightedScore / totalWeight).toFixed(2);
    },

    // Auto-calculate Section B Overall Self Rating (Simple Average)
    calculatedSectionB() {
      if (!this.attributes || this.attributes.length === 0) return null;

      let totalRating = 0;
      let count = 0;

      for (const attr of this.attributes) {
        if (attr.selfRating) {
          totalRating += parseFloat(attr.selfRating);
          count++;
        }
      }

      if (count === 0) return null;
      return (totalRating / count).toFixed(2);
    }
  },

  mounted() {
    this.loadEmployeeInfo();
    this.loadStandardAttributes();
    this.loadKPATemplates();
    this.loadFeedbackQuestions(); // NEW: Load all feedback questions dynamically
    this.loadTrainingPrograms(); // NEW: Load training programs dynamically
    this.initializeGoals();

    // Set default period (last 6 months)
    const today = new Date();
    const sixMonthsAgo = new Date(today);
    sixMonthsAgo.setMonth(today.getMonth() - 6);

    this.appraisal.periodEnd = today.toISOString().split('T')[0];
    this.appraisal.periodStart = sixMonthsAgo.toISOString().split('T')[0];
  },

  methods: {
    async loadEmployeeInfo() {
      // Load employee information from session/API
      try {
        const data = new FormData();
        data.append("function", "getEmployeeInfo");

        const res = await execute(data, authUrl);

        if (res && res.data && res.data.success) {
          const emp = res.data.data;
          this.employeeInfo = {
            id: emp.id,
            name: emp.fullName || `${emp.firstName} ${emp.lastName}`,
            position: emp.designation || emp.position,
            department: emp.departmentName || emp.department,
            location: emp.location || 'Nairobi, Kenya',
            dateOfJoining: emp.hireDate || emp.dateOfJoining
          };
        }
      } catch (error) {
        console.error("Failed to load employee info:", error);
      }
    },

    async loadStandardAttributes() {
      try {
        const data = new FormData();
        data.append("function", "getStandardAttributes");

        const res = await execute(data, authUrl);

        if (res && res.data && res.data.success) {
          this.attributes = res.data.data.map(attr => ({
            sNo: attr.sNo,
            attribute: attr.attribute,
            description: attr.description,
            selfRating: ''
          }));
        }
      } catch (error) {
        console.error("Failed to load attributes:", error);
        // Use default attributes if API fails
        this.loadDefaultAttributes();
      }
    },

    loadDefaultAttributes() {
      this.attributes = [
        { sNo: 1, attribute: 'Decision & Interface skills', selfRating: '' },
        { sNo: 2, attribute: 'Customer orientation', selfRating: '' },
        { sNo: 3, attribute: 'Domain understanding', selfRating: '' },
        { sNo: 4, attribute: 'Dependability & result orientation', selfRating: '' },
        { sNo: 5, attribute: 'Process orientation', selfRating: '' },
        { sNo: 6, attribute: 'Result orientation', selfRating: '' },
        { sNo: 7, attribute: 'Team work', selfRating: '' },
        { sNo: 8, attribute: 'Conflict Handling', selfRating: '' },
        { sNo: 9, attribute: 'Adaptability', selfRating: '' },
        { sNo: 10, attribute: 'Influencing & Persuading', selfRating: '' }
      ];
    },

    async loadFeedbackQuestions() {
      try {
        const data = new FormData();
        data.append("function", "getFeedbackQuestions");

        const res = await execute(data, authUrl);

        if (res && res.data && res.data.success) {
          const questions = res.data.data;
          this.organizationalParams = questions.organizationalParams || [];
          this.jobContent = questions.jobContent || [];
          this.growthOpportunities = questions.growthOpportunities || [];
          this.workCulture = questions.workCulture || [];
          this.colleagues = questions.colleagues || [];
        }
      } catch (error) {
        console.error("Failed to load feedback questions:", error);
        // Load defaults if API fails
        this.loadDefaultFeedbackQuestions();
      }
    },

    loadDefaultFeedbackQuestions() {
      this.organizationalParams = [
        { sNo: 1, question: 'Quality and productivity improvement?', answer: '', associateComments: '' },
        { sNo: 2, question: 'Executing higher responsibilities', answer: '', associateComments: '' },
        { sNo: 3, question: 'Worked for more than one customer at the same time', answer: '', associateComments: '' }
      ];

      this.jobContent = [
        { sNo: 1, attribute: 'Clarity on roles and responsibilities at work', response: '', remarks: '' },
        { sNo: 2, attribute: 'Usage of Expertise, knowledge and potential', response: '', remarks: '' },
        { sNo: 3, attribute: 'Job freedom', response: '', remarks: '' },
        { sNo: 4, attribute: 'Opportunities to Grow', response: '', remarks: '' }
      ];

      this.growthOpportunities = [
        { sNo: 1, attribute: 'Handle higher responsibilities and explore new avenues within the job premises', response: '', remarks: '' },
        { sNo: 2, attribute: 'Growth Opportunities available to the associate in the organization', response: '', remarks: '' },
        { sNo: 3, attribute: 'Opportunities to learn and enhance associates\' skills for professional growth', response: '', remarks: '' }
      ];

      this.workCulture = [
        { sNo: 1, attribute: 'Express views and opinions on issues affecting associate and organization', response: '', remarks: '' },
        { sNo: 2, attribute: 'The policies and processes are transparent', response: '', remarks: '' },
        { sNo: 3, attribute: 'Company work environment', response: '', remarks: '' },
        { sNo: 4, attribute: 'Friends at work place', response: '', remarks: '' },
        { sNo: 5, attribute: 'Work Interest', response: '', remarks: '' }
      ];

      this.colleagues = [
        { sNo: 1, attribute: 'Helpful and cooperative', response: '', remarks: '' },
        { sNo: 2, attribute: 'Potentiality and coherence of the team', response: '', remarks: '' },
        { sNo: 3, attribute: 'Discuss and share all relevant information with the associate', response: '', remarks: '' },
        { sNo: 4, attribute: 'Guidance', response: '', remarks: '' }
      ];
    },

    async loadTrainingPrograms() {
      try {
        const data = new FormData();
        data.append("function", "getTrainingPrograms");

        const res = await execute(data, authUrl);

        if (res && res.data && res.data.success) {
          this.trainings = res.data.data.map(training => ({
            sNo: training.sNo,
            trainingProgram: training.trainingProgram,
            priority: ''
          }));
        }
      } catch (error) {
        console.error("Failed to load training programs:", error);
        // Load defaults if API fails
        this.loadDefaultTrainingPrograms();
      }
    },

    loadDefaultTrainingPrograms() {
      this.trainings = [
        { sNo: 1, trainingProgram: 'Change Management', priority: '' },
        { sNo: 2, trainingProgram: 'Customer Communications', priority: '' },
        { sNo: 3, trainingProgram: 'Effective Communication and Interpersonal Skills', priority: '' },
        { sNo: 4, trainingProgram: 'Employee Engagement', priority: '' },
        { sNo: 5, trainingProgram: 'Leadership', priority: '' },
        { sNo: 6, trainingProgram: 'Organizational Development', priority: '' },
        { sNo: 7, trainingProgram: 'Presentation Effectiveness', priority: '' }
      ];
    },

    async loadKPATemplates() {
      try {
        const data = new FormData();
        data.append("function", "getKPATemplates");
        data.append("department", this.employeeInfo.department);

        const res = await execute(data, authUrl);

        if (res && res.data && res.data.success) {
          this.kpas = res.data.data.map(kpa => ({
            sNo: kpa.sNo,
            kpa: kpa.kpa,
            resultsAchieved: '',
            selfRating: '',
            weightage: kpa.defaultWeightage || 10
          }));
        } else {
          // Add one empty KPA if no templates found
          this.addKPA();
        }
      } catch (error) {
        console.error("Failed to load KPA templates:", error);
        this.addKPA();
      }
    },

    initializeGoals() {
      // Add 3 empty goals by default
      for (let i = 0; i < 3; i++) {
        this.addGoal();
      }
    },

    addKPA() {
      this.kpas.push({
        sNo: this.kpas.length + 1,
        kpa: '',
        resultsAchieved: '',
        selfRating: '',
        weightage: 10
      });
    },

    removeKPA(index) {
      if (this.kpas.length > 1) {
        this.kpas.splice(index, 1);
        // Renumber
        this.kpas.forEach((kpa, i) => {
          kpa.sNo = i + 1;
        });
      }
    },

    addGoal() {
      this.goals.push({
        sNo: this.goals.length + 1,
        actionPlan: '',
        targetDate: ''
      });
    },

    removeGoal(index) {
      if (this.goals.length > 1) {
        this.goals.splice(index, 1);
        // Renumber
        this.goals.forEach((goal, i) => {
          goal.sNo = i + 1;
        });
      }
    },

    goToSection(index) {
      if (index >= 0 && index < this.sections.length) {
        this.currentSection = index;
        window.scrollTo(0, 0);
      }
    },

    nextSection() {
      if (this.validateCurrentSection()) {
        this.currentSection++;
        window.scrollTo(0, 0);
      }
    },

    previousSection() {
      if (this.currentSection > 0) {
        this.currentSection--;
        window.scrollTo(0, 0);
      }
    },

    validateCurrentSection() {
      switch (this.currentSection) {
        case 0: // Section A
          if (this.kpas.length === 0) {
            this.$toast?.error("Please add at least one KPA") || alert("Please add at least one KPA");
            return false;
          }
          for (let kpa of this.kpas) {
            if (!kpa.kpa || !kpa.selfRating) {
              this.$toast?.error("Please complete all KPA fields") || alert("Please complete all KPA fields");
              return false;
            }
          }
          return true;

        case 1: // Section B
          for (let attr of this.attributes) {
            if (!attr.selfRating) {
              this.$toast?.error("Please rate all attributes") || alert("Please rate all attributes");
              return false;
            }
          }
          return true;

        case 2: // Section C Part 1
          if (!this.jobContent.every(j => j.response) ||
              !this.growthOpportunities.every(g => g.response) ||
              !this.workCulture.every(w => w.response)) {
            this.$toast?.error("Please answer all feedback questions") || alert("Please answer all feedback questions");
            return false;
          }
          return true;

        case 3: // Section C Part 2
          if (!this.colleagues.every(c => c.response)) {
            this.$toast?.error("Please answer all colleague questions") || alert("Please answer all colleague questions");
            return false;
          }
          if (!this.trainings.every(t => t.priority)) {
            this.$toast?.error("Please prioritize all training programs") || alert("Please prioritize all training programs");
            return false;
          }
          return true;

        case 4: // Section E
          for (let goal of this.goals) {
            if (!goal.actionPlan || !goal.targetDate) {
              this.$toast?.error("Please complete all goals") || alert("Please complete all goals");
              return false;
            }
          }
          return true;

        default:
          return true;
      }
    },

    async saveDraft() {
      this.saving = true;

      try {
        const payload = this.preparePayload();
        payload.status = 'Draft';

        const data = new FormData();
        data.append("function", "saveAppraisalDraft");
        data.append("appraisalData", JSON.stringify(payload));
        if (this.appraisalId) {
          data.append("appraisalId", this.appraisalId);
        }

        const res = await execute(data, authUrl);

        if (res && res.data && res.data.success) {
          this.appraisalId = res.data.data.appraisalId;
          this.$toast?.success("Draft saved successfully") || alert("Draft saved successfully");
        } else {
          throw new Error(res?.data?.message || "Failed to save draft");
        }
      } catch (error) {
        console.error("Error saving draft:", error);
        this.$toast?.error("Failed to save draft") || alert("Failed to save draft");
      } finally {
        this.saving = false;
      }
    },

    async submitAppraisal() {
      if (!this.signatureAgreement) {
        this.$toast?.error("Please agree to the digital signature") || alert("Please agree to the digital signature");
        return;
      }

      // Validate all sections
      for (let i = 0; i < this.sections.length - 1; i++) {
        this.currentSection = i;
        if (!this.validateCurrentSection()) {
          return;
        }
      }

      this.submitting = true;

      try {
        const payload = this.preparePayload();
        payload.status = 'Self-Assessment Complete';
        payload.selfAssessmentDate = new Date().toISOString();
        payload.employeeSignatureDate = new Date().toISOString().split('T')[0];

        const data = new FormData();
        data.append("function", "submitSelfAppraisal");
        data.append("appraisalData", JSON.stringify(payload));
        if (this.appraisalId) {
          data.append("appraisalId", this.appraisalId);
        }

        const res = await execute(data, authUrl);

        if (res && res.data && res.data.success) {
          this.$toast?.success("Appraisal submitted successfully!") || alert("Appraisal submitted successfully!");
          setTimeout(() => {
            this.$router.push('/my-appraisals');
          }, 2000);
        } else {
          throw new Error(res?.data?.message || "Failed to submit appraisal");
        }
      } catch (error) {
        console.error("Error submitting appraisal:", error);
        this.$toast?.error("Failed to submit appraisal") || alert("Failed to submit appraisal");
      } finally {
        this.submitting = false;
      }
    },

    preparePayload() {
      return {
        employeeID: this.employeeInfo.id,
        appraisalYear: this.appraisalYear,
        periodStart: this.appraisal.periodStart,
        periodEnd: this.appraisal.periodEnd,
        positionTitle: this.employeeInfo.position,
        department: this.employeeInfo.department,
        location: this.employeeInfo.location,
        dateOfJoining: this.employeeInfo.dateOfJoining,
        associateComments: this.appraisal.associateComments,
        kpas: this.kpas,
        organizationalParams: this.organizationalParams,
        attributes: this.attributes,
        jobContent: this.jobContent,
        growthOpportunities: this.growthOpportunities,
        workCulture: this.workCulture,
        colleagues: this.colleagues,
        trainings: this.trainings,
        additionalFeedback: this.additionalFeedback,
        goals: this.goals
      };
    },

    getSectionStatus(index) {
      // Logic to determine section status
      return 'Pending';
    },

    getSectionCompletionBadge(index) {
      return 'bg-warning';
    }
  }
};
</script>

<style scoped>
.table td, .table th {
  vertical-align: middle;
}

.badge {
  font-size: 0.85rem;
  padding: 0.35em 0.65em;
}

textarea.form-control {
  resize: vertical;
}

.card {
  margin-bottom: 1.5rem;
}

.progress {
  border-radius: 10px;
}

.progress-bar {
  transition: width 0.3s ease;
}
</style>